import type { ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Spinner } from './spinner';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type Size = 'sm' | 'md' | 'lg' | 'icon';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-brand text-white hover:bg-brand-hover active:bg-brand-active border border-transparent shadow-raised',
  secondary:
    'bg-surface-2 text-fg hover:bg-surface-3 border border-line-strong',
  ghost:
    'bg-transparent text-fg-muted hover:text-fg hover:bg-surface-2 border border-transparent',
  danger:
    'bg-danger/12 text-danger hover:bg-danger/20 border border-danger/30',
  success:
    'bg-success/12 text-success hover:bg-success/20 border border-success/30',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-sm gap-2 rounded-lg',
  lg: 'h-11 px-5 text-sm gap-2 rounded-lg',
  icon: 'h-9 w-9 justify-center rounded-lg',
};

const ICON_SIZE: Record<Size, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-4 w-4',
  icon: 'h-4 w-4',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  fullWidth?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon: Icon,
  iconRight: IconRight,
  fullWidth,
  className,
  children,
  disabled,
  type = 'button',
  ...props
}: ButtonProps) {
  const iconClass = ICON_SIZE[size];
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center font-medium whitespace-nowrap transition-colors duration-150',
        'disabled:pointer-events-none disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full justify-center',
        className
      )}
      {...props}
    >
      {loading ? (
        <Spinner className={iconClass} />
      ) : (
        Icon && <Icon className={cn(iconClass, 'shrink-0')} aria-hidden="true" />
      )}
      {children}
      {IconRight && !loading && <IconRight className={cn(iconClass, 'shrink-0')} aria-hidden="true" />}
    </button>
  );
}
